import { Loader2, X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  useLayoutEffect,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";

import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { isGooglePickerTarget } from "@/hooks/googlePickerTarget";
import { cn } from "@/lib/utils";

export type UploadPreview = {
  /** The previewed card's id; the overlay pops open from that card. */
  id: string;
  name: string;
  onClose: () => void;
  body: ReactNode;
};

export type UploadAction = {
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  testId?: string;
};

type Props = {
  open: boolean;
  /** Escape, the overlay or the rail's close button. */
  onRequestClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** The file rail; the dialog widens and the rail slides in while it is set. */
  rail: ReactNode | null;
  surface: ReactNode;
  /** Under the surface (options that apply to every file). */
  below?: ReactNode;
  preview?: UploadPreview | null;
  summary?: ReactNode;
  onCancel: () => void;
  cancelDisabled?: boolean;
  action: UploadAction;
  testId?: string;
  /** Nested dialogs (a discard confirmation, a picker). */
  children?: ReactNode;
};

/**
 * The upload dialog every file upload uses: a drop surface, a rail of the
 * files about to be added once there are any, a preview that pops open from
 * the clicked card, and a footer with a summary, Cancel and the add action.
 * What a file's card says and what the action does are the host's.
 */
export const UploadDialogShell: FC<Props> = ({
  open,
  onRequestClose,
  title,
  description,
  rail,
  surface,
  below,
  preview,
  summary,
  onCancel,
  cancelDisabled,
  action,
  testId = "upload-dialog",
  children
}) => {
  const reduce = useReducedMotion();
  const contentRef = useRef<HTMLDivElement>(null);
  const [originY, setOriginY] = useState(96);
  const hasRail = rail != null;
  const previewId = preview?.id ?? null;

  // Pop the preview open from the clicked card's vertical centre (genie).
  useLayoutEffect(() => {
    if (!previewId) return;
    const escaped =
      typeof CSS !== "undefined" && CSS.escape
        ? CSS.escape(previewId)
        : previewId;
    const card = document.querySelector<HTMLElement>(
      `[data-card-id="${escaped}"]`
    );
    const box = contentRef.current;
    if (!card || !box) return;
    const cardRect = card.getBoundingClientRect();
    const boxRect = box.getBoundingClientRect();
    setOriginY(
      Math.min(
        Math.max(24, cardRect.top + cardRect.height / 2 - boxRect.top),
        Math.max(24, boxRect.height - 24)
      )
    );
  }, [previewId]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onRequestClose();
      }}
    >
      <DialogContent
        className={cn(
          "flex gap-0 overflow-hidden rounded-2xl p-0 shadow-dialog ring-0",
          reduce ? "" : "transition-[max-width,height] duration-300 ease-out",
          hasRail
            ? "h-[95vh] sm:max-w-[95vw]"
            : "h-auto max-h-[82vh] sm:max-w-xl"
        )}
        data-testid={testId}
        showCloseButton={false}
        onInteractOutside={(event) => {
          if (isGooglePickerTarget(event.target)) event.preventDefault();
        }}
        onFocusOutside={(event) => {
          if (isGooglePickerTarget(event.target)) event.preventDefault();
        }}
      >
        <AnimatePresence initial={false}>
          {hasRail ? (
            <motion.aside
              key="rail"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 360, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{
                duration: reduce ? 0 : 0.3,
                ease: [0.4, 0, 0.2, 1]
              }}
              className="flex min-h-0 shrink-0 flex-col overflow-hidden border-r border-line-2 bg-fill-1"
            >
              <div className="flex h-14 w-90 shrink-0 items-center px-3">
                <DialogClose asChild>
                  <Button variant="ghost" size="icon-sm" aria-label="Close">
                    <X />
                  </Button>
                </DialogClose>
              </div>
              <div className="min-h-0 w-90 flex-1 px-3 pb-3">{rail}</div>
            </motion.aside>
          ) : null}
        </AnimatePresence>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="text-xl font-medium">{title}</DialogTitle>
            {description ? (
              <DialogDescription>{description}</DialogDescription>
            ) : null}
          </DialogHeader>

          <div
            ref={contentRef}
            className="relative flex min-h-0 flex-1 flex-col"
          >
            <div className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto p-6">
              <div className="my-auto flex w-full max-w-xl flex-col gap-4">
                {surface}
                {below}
              </div>
            </div>

            <AnimatePresence>
              {preview ? (
                <motion.div
                  key="preview"
                  className="absolute inset-3 z-20 flex flex-col overflow-hidden rounded-xl bg-elevated shadow-dialog ring-1 ring-line-2"
                  initial={{ opacity: 0, scale: reduce ? 1 : 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: reduce ? 1 : 0.92 }}
                  transition={{
                    duration: reduce ? 0 : 0.22,
                    ease: [0.22, 1, 0.36, 1]
                  }}
                  style={{ transformOrigin: `left ${originY}px` }}
                  data-testid="upload-preview"
                >
                  <div className="flex h-9 shrink-0 items-center gap-2 border-b border-line-1 bg-fill-1 px-3">
                    <FileTypeIcon name={preview.name} size={16} />
                    <span
                      className="truncate text-sm font-medium"
                      title={preview.name}
                    >
                      {preview.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      className="ml-auto"
                      aria-label="Close preview"
                      onClick={preview.onClose}
                      data-testid="upload-preview-close"
                    >
                      <X className="size-3.5" />
                    </Button>
                  </div>
                  <div className="min-h-0 flex-1">{preview.body}</div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          <DialogFooter className="mx-0 mb-0 items-center rounded-none border-t border-line-1 px-6 py-4 sm:justify-between">
            <span className="text-sm text-fg-3" data-testid="upload-summary">
              {summary}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="md"
                onClick={onCancel}
                disabled={cancelDisabled}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={action.onClick}
                disabled={action.disabled}
                data-testid={action.testId ?? "upload-commit"}
              >
                {action.busy ? <Loader2 className="animate-spin" /> : null}
                {action.label}
              </Button>
            </div>
          </DialogFooter>
        </div>
        {children}
      </DialogContent>
    </Dialog>
  );
};
